/**
 * viewportContextController — FSM driver for PSPACE ↔ MSPACE_VIA_VP transitions.
 *
 * AutoCAD parity (MC4 Slice 2 — true in-place): dblclick inside a paper-space
 * viewport → enter MSPACE_VIA_VP. The layout sheet + every viewport's model
 * render stay on screen; the active VP is edited in-place inside its rectangle.
 * Esc → back to PSPACE. The main camera does NOT move on enter or exit —
 * rendering of the model inside each VP is handled by ViewportRenderOrchestrator
 * (per-VP scissor passes), which now runs in MSPACE_VIA_VP as well as PSPACE.
 *
 * FSM transitions:
 *   PSPACE  —dblclick(inside VP)→  MSPACE_VIA_VP(vpId)
 *   MSPACE_VIA_VP  —Esc→  PSPACE
 *   MSPACE_VIA_VP  —dblclick(different VP)→  MSPACE_VIA_VP(newVpId)  [VP switch]
 *   PSPACE  —dblclick(outside VP)→  PSPACE  [no-op]
 *
 * Camera management:
 *   - On enter: NO camera change (no dive, no twist, no snapshot). The main
 *     camera stays on the paper view; the dblclick hit-test uses the live
 *     (paper) camera to decide which VP was clicked.
 *   - On exit: NO camera change (nothing was moved, nothing to restore).
 *
 * NOTE (MC4 Slice 2 scope): switching the on-screen render to true in-place
 * intentionally breaks in-VP hover / selection / drawing until a later slice
 * adds inverse-transform input routing (screen ↔ VP-local model coords). This
 * slice only changes the RENDER + neutralizes the camera dive.
 *
 * UCS per-viewport: not yet in ViewportEntity schema (MC5 scope).
 */

import * as THREE from "three";

import { readViewportOverrides } from "@dxf-ai-editor/drawing-model";
import { useSpaceContextStore } from "../../state/spaceContextStore";
import type { CameraStateSnapshot } from "../cameraApi";
import type { PendingCameraTween } from "../cameraApiBinder";
import { pointInViewport } from "../viewports/viewportTransformAdapter";

export interface ViewportLike {
  id: string;
  center: { x: number; y: number };
  width: number;
  height: number;
  viewCenter: { x: number; y: number };
  viewHeight: number;
  viewTwistAngle: number;
  /**
   * MC5 — AutoCAD parity: a locked VP cannot be entered via dblclick.
   * Mirrors `ViewportEntity.displayLocked`. Optional so existing callers /
   * unit-test stubs that omit it still compile.
   */
  displayLocked?: boolean;
  /** Optional XDATA for reading MC5 override-lock persisted in EDIT_DWG_VP_OVERRIDES. */
  xdata?: { appName: string; groups: { code: number; value: string }[] }[];
  frozenLayers?: string[];
}

export interface SavedPaperState {
  camera: CameraStateSnapshot;
  upX: number;
  upY: number;
  upZ: number;
}

export interface ViewportContextControllerArgs {
  /** Overlay shell div — canvas is its first <canvas> child. */
  overlayShell: HTMLDivElement;
  /** Live THREE OrthographicCamera ref (populated inside R3F Canvas). */
  cameraRef: React.MutableRefObject<THREE.OrthographicCamera | null>;
  /** Paper/model camera state snapshot (pixels per world unit). */
  cameraStateRef: React.MutableRefObject<CameraStateSnapshot>;
  /** Pending tween slot consumed by CameraApiBinder. */
  pendingTweenRef: React.MutableRefObject<PendingCameraTween | null>;
  /** Canvas CSS-pixel size. */
  viewportSizeRef: React.MutableRefObject<{ width: number; height: number }>;
  /** Trigger a demand-mode frame redraw. */
  invalidateRef: React.MutableRefObject<(() => void) | null>;
  /** Active layout viewports (filtered list, excludes control VP). */
  viewports: ReadonlyArray<ViewportLike>;
  /**
   * Paper-space sheet origin added to VP center coordinates, in the
   * renderer's OFFSET-world frame (`Scene3DStage`'s `paperOriginOffset`) —
   * the same frame `screenToPaper` unprojects into, so the dblclick
   * hit-test compares like with like.
   */
  paperOrigin: { x: number; y: number };
  /**
   * External ref for persisting paper-camera snapshot across effect reinstalls.
   * When provided, saved state survives React effect re-runs triggered by
   * viewport-list identity changes (Fix #4 — Codex P1 #165).
   * Uses structural { current } shape so both React.MutableRefObject and plain
   * objects satisfy the constraint without needing a React import here.
   */
  paperSnapshotRef?: { current: SavedPaperState | null };
  /**
   * MC4 iter-7 P2 #6: External ref for persisting exitTweenActive flag across
   * controller reinstalls (activeLayoutViewports identity change resets closure-
   * local let; hoisting to a ref mirrors the paperSnapshotRef pattern from Fix #4).
   * When provided, the exit-tween reentry guard survives React effect re-runs.
   * Kept optional so unit-test stubs that omit it still compile.
   */
  exitTweenRef?: { current: boolean };
  /**
   * MC4 iter-4 P2 #165: Called after each enter/exit transition so the
   * caller can mirror the layout context to editorStore.drawing.layoutContext.
   * "viewport" → entering a VP (MSPACE_VIA_VP); "paper" → returning to PSPACE.
   * Kept optional so unit-test stubs that omit it still compile.
   */
  onLayoutContextChange?: (ctx: "paper" | "viewport") => void;
}

/**
 * Install dblclick + keydown listeners that drive the PSPACE ↔ MSPACE_VIA_VP FSM.
 * Returns a teardown function that removes all listeners.
 */
export function installViewportContextController(
  args: ViewportContextControllerArgs,
): () => void {
  const canvas = args.overlayShell.querySelector(
    "canvas",
  ) as HTMLCanvasElement | null;
  if (!canvas) return () => {};

  // MC4 Slice 2 — the camera-dive machinery (paper snapshot holder, exit-tween
  // guard, scheduleTween, applyCameraTwist, resetCameraUp, vpZoom,
  // buildTempPaperCamera) was removed: entering a viewport no longer moves the
  // main camera, so there is nothing to snapshot, tween, or restore. The
  // ViewportContextControllerArgs interface still exposes the camera/tween refs
  // (paperSnapshotRef, exitTweenRef, pendingTweenRef, cameraStateRef,
  // invalidateRef, viewportSizeRef) for callers + future slices; they are
  // simply unused by the in-place render path.

  // ── helpers ────────────────────────────────────────────────────────────────

  /** Convert screen event coords to paper-world point via the given camera. */
  function screenToPaper(
    e: MouseEvent,
    cam: THREE.Camera,
  ): { x: number; y: number } {
    const rect = canvas!.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const v = new THREE.Vector3(ndcX, ndcY, 0).unproject(cam);
    return { x: v.x, y: v.y };
  }

  // ── FSM actions ────────────────────────────────────────────────────────────

  function doEnterViewport(vp: ViewportLike): void {
    // MC5 — AutoCAD parity: locked viewports cannot be entered via dblclick.
    // Effective lock = entity field OR override XDATA (whichever is set).
    const overrideLocked = readViewportOverrides(vp).locked;
    const effectiveLocked = vp.displayLocked === true || overrideLocked;
    if (effectiveLocked) return;

    const store = useSpaceContextStore.getState();
    if (store.mode === "MSPACE_VIA_VP" && store.activeViewportId === vp.id) {
      return; // same VP — no-op
    }

    // MC4 Slice 2 — AutoCAD in-place parity: entering a viewport NO LONGER
    // dives the main camera into the VP's model view. The layout sheet + every
    // VP's model render stay on screen (ViewportRenderOrchestrator renders in
    // MSPACE_VIA_VP too); the active VP is edited in-place inside its
    // rectangle. The main camera must NOT move on enter — so there is no paper
    // snapshot to save (nothing to restore) and no tween to schedule.
    //
    // Switching VP from one to another also requires no camera change: just
    // re-target the active VP via the FSM. (enterViewport requires PSPACE, so
    // exit the current MSPACE_VIA_VP state first.)
    if (store.mode === "MSPACE_VIA_VP") {
      store.exitViewport();
    }
    store.enterViewport(vp.id);
    // MC4 iter-4 P2 #165: mirror to editorStore.drawing.layoutContext.
    args.onLayoutContextChange?.("viewport");
  }

  function doExitViewport(): void {
    const store = useSpaceContextStore.getState();
    if (store.mode !== "MSPACE_VIA_VP") return;
    store.exitViewport();
    // MC4 iter-4 P2 #165: mirror to editorStore.drawing.layoutContext.
    args.onLayoutContextChange?.("paper");

    // MC4 Slice 2 — the main camera never moved on enter (no dive, no twist),
    // so there is nothing to restore on Esc: no return tween, no snapshot to
    // clear. The camera up is already (0,1,0) since the dive twist was removed.
    // The orchestrator simply reverts to the PSPACE highlight (no active VP).
  }

  // ── event handlers ─────────────────────────────────────────────────────────

  function onDblClick(e: MouseEvent): void {
    const store = useSpaceContextStore.getState();
    const mode = store.mode;
    if (mode !== "PSPACE" && mode !== "MSPACE_VIA_VP") return;

    // MC4 Slice 2 — the main camera always stays on the paper view (no dive on
    // enter), so the live camera IS the paper camera in BOTH PSPACE and
    // MSPACE_VIA_VP. The hit-test that decides WHICH VP was double-clicked uses
    // the live camera directly in either mode — no saved-snapshot detour or
    // temp-camera reconstruction is needed now that the camera never moves.
    const liveCam = args.cameraRef.current;
    if (!liveCam) return;
    const paperPt = screenToPaper(e, liveCam);

    const hitVp = args.viewports.find((vp) =>
      pointInViewport(paperPt, vp, args.paperOrigin),
    );
    if (!hitVp) return; // dblclick outside any VP → no-op

    doEnterViewport(hitVp);
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Escape") return;
    doExitViewport();
  }

  canvas.addEventListener("dblclick", onDblClick);
  window.addEventListener("keydown", onKeyDown);

  return () => {
    canvas.removeEventListener("dblclick", onDblClick);
    window.removeEventListener("keydown", onKeyDown);
  };
}
